import { NotFound } from 'fejl'
import { computed, observable, action, makeObservable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import Workspace from 'iam/models/Workspace'
import SessionStore from 'iam/SessionStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import WorkspaceStore from 'iam/WorkspaceStore'
import { LastWorkspaceStorage } from 'infra/LastWorkspace'
import { localStorage as storage } from 'infra/storage'

export default class WorkspaceSelectorState {
  @observable
  searchText = ''

  @observable
  workspace: Workspace | null = null

  @observable
  isSelectorOpen = false

  @observable
  isDefaultToAllWorkspaces = false

  constructor(
    private workspaceMembershipsStore: WorkspaceMembershipStore,
    private workspaceStore: WorkspaceStore,
    private sessionStore: SessionStore,
    private checkMemberExists: (workspace: Workspace) => Promise<boolean>,
    private lastWorkspaceStorage: LastWorkspaceStorage,
  ) {
    makeObservable(this)

    runInAction(() => {
      const stored = storage.getItem('WorkspaceSelector.DefaultToAllWorkspaces')
      this.isDefaultToAllWorkspaces = stored === 'true'
    })
  }

  async activate(inviteWorkspace?: Workspace) {
    await this.preSelectWorkspace(inviteWorkspace)
  }

  /**
   * The current user's workspace memberships.
   */
  @computed
  get memberships() {
    return this.workspaceMembershipsStore.memberships.slice()
  }

  /**
   * The current user's workspace memberships, sorted by name.
   */
  @computed
  get sortedMemberships() {
    return this.memberships
      .slice()
      .sort((a, b) => a.workspace.name.localeCompare(b.workspace.name))
  }

  /**
   * User's workspaces that have been filtered.
   */
  @computed
  get filteredMemberships() {
    return this.sortedMemberships.filter(
      (x) =>
        x.workspace.name
          .toLowerCase()
          .includes(this.searchText.toLowerCase()) ||
        x.workspace.slug.toLowerCase().includes(this.searchText.toLowerCase()),
    )
  }

  @action.bound
  setSearchText(text?: string) {
    this.searchText = text || ''
  }

  @action.bound
  toggleSelector() {
    this.isSelectorOpen = !this.isSelectorOpen
  }

  @action.bound
  openSelector() {
    this.isSelectorOpen = true
  }

  @action.bound
  closeSelector() {
    this.isSelectorOpen = false
  }

  @action.bound
  toggleDefaultToAllWorkspaces() {
    this.isDefaultToAllWorkspaces = !this.isDefaultToAllWorkspaces
    storage.setItem(
      'WorkspaceSelector.DefaultToAllWorkspaces',
      this.isDefaultToAllWorkspaces ? 'true' : 'false',
    )
  }

  selectWorkspace = task.resolved(async (workspace: Workspace) => {
    if (!workspace) {
      return
    }

    // Make sure we have the most up-to-date version of the workspace.
    const freshWorkspace = await this.workspaceStore.fetch(workspace.id)

    if (!freshWorkspace) {
      throw new Error('The workspace you selected does not exist.')
    }

    if (await this.checkMemberExists(freshWorkspace)) {
      // The workspace and member exists, so we can select it in the session store.
      // This will make the WorkspaceGateway render the actual app.
      await this.sessionStore.selectWorkspace(freshWorkspace)
    }
    this.closeSelector()
  })

  /**
   * Attempts to pre-select a workspace based on the user's last visit.
   */
  async preSelectWorkspace(
    inviteWorkspace?: Workspace,
    workspaceSlug?: string,
  ) {
    if (!this.sessionStore.isAuthenticated) {
      return false
    }
    if (inviteWorkspace) {
      if (await this.checkMemberExists(inviteWorkspace)) {
        return this.sessionStore.selectWorkspace(inviteWorkspace)
      }
    }

    if (workspaceSlug) {
      const workspace = this.workspaceStore.workspaces.find(
        (x) => x.slug === workspaceSlug,
      )
      if (workspace) {
        if (await this.checkMemberExists(workspace)) {
          return this.sessionStore.selectWorkspace(workspace)
        }
      }
    }

    const userId = this.sessionStore.user.id
    const lastSelectedWorkspaceId = userId
      ? this._getLastSelectedWorkspace(userId)
      : null

    let lastSelectedWorkspace: Workspace | undefined | null

    // Check if the last selected workspace is still part of the user's memberships.
    // Might be a case where the user was removed from the workspace and their last selected workspace was the one they got kicked out of.
    const isLastSelectedWorkspaceValid =
      this.workspaceMembershipsStore.memberships.find(
        (x) => x.workspace.id === lastSelectedWorkspaceId,
      ) !== undefined

    if (lastSelectedWorkspaceId && isLastSelectedWorkspaceValid) {
      lastSelectedWorkspace = this.workspaceStore.getWorkspaceByIdOrSlug(
        lastSelectedWorkspaceId,
      )
      if (!lastSelectedWorkspace) {
        lastSelectedWorkspace = await this.workspaceStore
          .fetchWorkspace(lastSelectedWorkspaceId)
          .catch(NotFound.ignore(null))
      }
    }

    if (
      !lastSelectedWorkspace &&
      this.workspaceMembershipsStore.memberships.length > 0
    ) {
      lastSelectedWorkspace =
        this.workspaceMembershipsStore.memberships[0].workspace
    }

    if (lastSelectedWorkspace) {
      await this.selectWorkspace(lastSelectedWorkspace)
      this.closeSelector()
    }
  }

  _getLastSelectedWorkspace(userId: string) {
    return this.lastWorkspaceStorage.load(userId)
  }
}
